import { useEffect, useState } from 'react';
import { Languages, LogOut, Mail, ShieldCheck, User } from 'lucide-react';
import { PasskeySection } from './PasskeySection';
import { SessionsSection } from './SessionsSection';
import { CloseAccountSection } from './CloseAccountSection';
import { DataExportSection } from './DataExportSection';
import { EraseAccountSection } from './EraseAccountSection';
import { fetchMe, type MeResult } from '../../lib/api/me';
import { showsLanguagePicker } from '../ui';
import type { ClubAccount, Scope } from '../../lib/auth';
import { ROLE_NAME, useLocale } from '../../i18n';
import { LanguagePicker } from '../ui';

// ─────────────────────────────────────────────────────────────────────
// MIN KONTO (#383).
//
// The smallest surface that answers the question a club administrator asks the
// moment they are handed a generated password: «how do I change this?»
//
// It shows who they are signed in as, changes their password, and signs them
// out. It is NOT a profile system: no avatar, no editable details, no
// preferences. Adding those is a product decision, not a gap to fill.
//
// WHAT IT DOES NOT DO: recovery. Somebody who cannot sign in never reaches this
// screen — that is #196, and it is a different problem with different rules.
//
// The identity shown is the one sign-in already returned. Fetching it again
// would be a request for data the client is holding.
// ─────────────────────────────────────────────────────────────────────

export interface AccountViewProps {
  /** The signed-in account, from the club session. */
  account: ClubAccount | null;
  /** The app's existing sign-out path — `signOutCustomerSessions()` upstream. */
  onSignOut: () => void;
  /**
   * The organizations sign-in returned, used HERE FOR THEIR NAMES ONLY.
   *
   * It is a snapshot taken when the session started, so the `roles` in it can
   * be stale by the time somebody opens this page — a revoked administrator
   * would still read «Administrator» (#817). «Dine rettigheter» therefore takes
   * every role from the fresh `/me` answer and joins to this list by
   * `tenantId` for one thing only: putting a human name on a tenant id. The
   * order is kept, because it is the order the scope switcher shows.
   */
  scopes?: Scope[];
  /** Injectable for tests: what the API says this account may do, right now. */
  loadMe?: typeof fetchMe;
}

// THE SYSTEM ROLES, NAMED FOR A PERSON, come from `i18n/roleNames.ts` — one
// map shared with the organization's «Tilganger» page (slice 3), so the two
// screens cannot name one role differently. A custom role has no entry and is
// shown by its key, which is still a name somebody chose.

export function AccountView({
  account,
  onSignOut,
  scopes = [],
  loadMe = fetchMe,
}: AccountViewProps) {
  const { t } = useLocale();

  // ── WHAT THE ACCOUNT MAY DO (asked for 2026-10-14) ──────────────────
  //
  // EVERY role on this page comes from one fresh `/me` call — platform and
  // organization alike. The sign-in snapshot is consulted for organization
  // NAMES and nothing else, because the tenant ids `/me` reports mean nothing
  // to a person.
  //
  // That split is the point of #817: grants move while a session lives. Another
  // administrator revokes TENANT_ADMIN, and the session's `scopes` keep it
  // until the next sign-in — so role chips rendered from them tell somebody
  // they are an administrator after they have stopped being one. Nothing here
  // decides anything; it reports what the server answered when the page
  // opened, and the server re-decides every call regardless.
  const accountId = account?.id ?? null;
  const [me, setMe] = useState<MeResult | null>(null);
  useEffect(() => {
    if (!accountId) return undefined;
    let live = true;
    // Cleared first: a second account must never read the previous one's roles
    // while its own answer is still in flight.
    setMe(null);
    void loadMe().then((r) => {
      if (live) setMe(r);
    });
    return () => {
      live = false;
    };
  }, [accountId, loadMe]);

  const roleName = (key: string) => {
    const k = ROLE_NAME[key];
    return k ? t(k) : key;
  };

  /** The server's answer, or null while it is in flight or did not arrive. */
  const rights = me?.state === 'ok' ? me.me.authorization : null;
  const platformRoles = rights ? [...rights.platformRoles].sort() : [];

  /**
   * The grants `/me` reports, named from the sign-in snapshot and shown in the
   * order the scope switcher uses. A grant the snapshot does not know about is
   * KEPT and labelled unknown: dropping it would hide a real right, and
   * borrowing a name for it would be a different falsehood than the one #817
   * removes. `/me` orders by tenant id, which is meaningless to read, so
   * anything unknown sorts last in that order rather than at random.
   */
  const organizationOrder = new Map(scopes.map((s, i) => [s.tenantId, i] as const));
  const organizations = (rights?.tenants ?? [])
    .map((tenant) => ({
      ...tenant,
      name: scopes.find((s) => s.tenantId === tenant.tenantId)?.name ?? null,
    }))
    .sort(
      (a, b) =>
        (organizationOrder.get(a.tenantId) ?? Number.MAX_SAFE_INTEGER) -
        (organizationOrder.get(b.tenantId) ?? Number.MAX_SAFE_INTEGER),
    );

  // The body of «I organisasjoner». Named states rather than ternaries stacked
  // inside the markup, because the one that matters is easy to miss: when the
  // server did not answer, this renders NOTHING. The old code fell back to the
  // sign-in snapshot, which is how a revoked administrator kept reading as one.
  const organizationsBody = () => {
    if (me === null) {
      return (
        <p className="mt-1.5 text-xs text-ink-3" data-testid="account-rights-loading">
          {t('account.rights.loading')}
        </p>
      );
    }
    // The line under the block says the roles could not be fetched. Saying
    // «no roles anywhere» here would be a guess about the same unknown.
    if (!rights) return null;
    if (organizations.length === 0) {
      return <p className="mt-1.5 text-xs text-ink-3">{t('account.rights.none')}</p>;
    }
    return (
      <ul className="mt-1.5 rounded-lg border border-line overflow-hidden">
        {organizations.map((o, i) => (
          <li
            key={o.tenantId}
            className={`flex items-center justify-between gap-3 px-3 py-2 ${
              i > 0 ? 'border-t border-line-soft' : ''
            }`}
          >
            <div className="min-w-0">
              <div
                className="text-sm font-medium text-ink truncate"
                title={o.name ? undefined : o.tenantId}
              >
                {o.name ?? t('account.rights.unknownOrg')}
              </div>
              <div className="text-[11px] text-ink-3">
                {o.kind === 'CENTRE' ? t('account.kind.CENTRE') : t('account.kind.CLUB')}
              </div>
            </div>
            {/* NO «Spiller» BRANCH (#861). It drew an organization with an
                empty role list, which `/me` cannot report: an organization
                appears there only through a `TenantMembership` row, every row
                has a role (`loadAccess`, apps/api/src/auth/authorization.ts),
                and a player is somebody with NO row — so a club where the
                account only plays is absent, not listed as «Spiller». */}
            <div className="flex flex-wrap justify-end gap-1.5">
              {o.roles.map((key) => (
                <span
                  key={key}
                  className="rounded-full border border-line bg-bg-soft px-2.5 py-0.5 text-xs text-ink"
                  title={key}
                >
                  {roleName(key)}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 flex flex-col gap-6">
      <section className="card p-5">
        <h1 className="font-display text-2xl font-bold text-ink">{t('account.title')}</h1>
        <div className="mt-4 flex flex-col gap-2 text-ink-2">
          {account?.displayName && (
            <span className="inline-flex items-center gap-2">
              <User size={16} /> {account.displayName}
            </span>
          )}
          <span className="inline-flex items-center gap-2">
            <Mail size={16} /> {account?.email ?? t('account.email.unknown')}
          </span>
        </div>
      </section>

      {/* DINE RETTIGHETER. The roles, not the capabilities: a person reads
          «Administrator i Oslo Padel» and knows what it means, where a list of
          thirty capability keys tells them nothing. What each role may do is
          decided in /sa. */}
      {account && (
        <section className="card p-5" data-testid="account-rights">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <ShieldCheck size={18} /> {t('account.rights.title')}
          </h2>
          <p className="mt-1 text-xs text-ink-3">{t('account.rights.lead')}</p>

          {platformRoles.length > 0 && (
            <div className="mt-4" data-testid="account-rights-platform">
              <div className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('account.rights.platform')}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {platformRoles.map((key) => (
                  <span
                    key={key}
                    className="rounded-full border border-lime-deep bg-lime-bg px-2.5 py-0.5 text-xs font-medium text-ink"
                    title={key}
                  >
                    {roleName(key)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4" data-testid="account-rights-organizations">
            <div className="font-mono uppercase tracking-wider text-xs text-ink-3">
              {t('account.rights.organizations')}
            </div>
            {organizationsBody()}
          </div>

          {me !== null && me.state !== 'ok' && (
            <p className="mt-3 text-xs text-ink-3" data-testid="account-rights-unavailable">
              {t('account.rights.unavailable')}
            </p>
          )}
        </section>
      )}

      {/* THE LAST LANGUAGE PICKER IN THE PRODUCT (#533). The two flags — the
          header and the login screen — are parked while i18n is
          deprioritised; this one stays reachable for the accounts named in
          `showsLanguagePicker`, so the work can still be exercised without
          offering a half-translated app to a club. */}
      {showsLanguagePicker(account?.email) && (
        <section className="card p-5">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <Languages size={18} /> {t('account.language')}
          </h2>
          {/* SWITCHES IN PLACE (#310a) — no reload, so nothing open is lost. */}
          <div className="mt-4 flex flex-col gap-2">
            <label className="sr-only" htmlFor="locale-select">
              {t('account.language')}
            </label>
            {/* THE SAME CONTROL AS THE HEADER AND THE LOGIN SCREEN (#310).
              Two implementations of one picker is how the three of them end
              up naming the languages differently — and the names are the part
              that has to be right. */}
            <LanguagePicker id="locale-select" className="w-full" />
            <p className="text-xs text-ink-3">{t('account.language.help')}</p>
          </div>
        </section>
      )}

      {/* THE PASSKEY SECTION IS THE CREDENTIAL SECTION NOW (#754).
          //
          // It used to sit above a change-password card, ordered «by the model
          // rather than by the order the two were built in». The password card
          // is gone: a credential no surface accepts is not something to offer
          // somebody a form for, and a change-password box under a sign-in
          // that cannot use one is an invitation to waste a minute.
          //
          // What is NOT removed is the reset-LINK landing: a platform
          // administrator still provisions a new club user that way, and that
          // journey is its own decision. See the PR. */}
      <PasskeySection />
      {/* «INNLOGGINGER» (#1141): sign out everywhere else, beside the passkeys. */}
      {account && <SessionsSection />}

      {/* «AVSLUTT KONTOEN» (#1062): the account side of the same closure the
          player app offers under «Min profil». Signed-out afterwards through
          the same path as the button below — the server has already revoked
          the session. */}
      {/* «LAST NED MINE DATA» (#1062): the same export the player app offers. */}
      {account && <DataExportSection />}
      {account && <CloseAccountSection onClosed={onSignOut} />}
      {/* «SLETT KONTOEN MIN OG DATAENE MINE» (#1062): erasure includes closure,
          and the session is gone afterwards — out through the same sign-out. */}
      {account && <EraseAccountSection onErased={onSignOut} />}

      <section className="card p-5 flex items-center justify-between gap-3">
        <span className="text-ink-2 text-sm">{t('app.signOutBrowser')}</span>
        <button type="button" className="btn-outline" onClick={onSignOut}>
          <LogOut size={14} />
          {t('app.signOut')}
        </button>
      </section>
    </div>
  );
}
